import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { BRAND, AI_DISCLAIMER } from "@/config/brand";
import api, { API } from "@/lib/api";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [googleMode, setGoogleMode] = useState("emergent");
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    // Which Google flow to use: Maziara's own client ("own") or the
    // Emergent-managed fallback ("emergent"). Server decides via config.
    api.get("/auth/mode").then((r) => setGoogleMode(r.data.google)).catch(() => {});
    if (new URLSearchParams(window.location.search).get("auth_error")) setAuthError(true);
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    navigate(user.onboarded ? "/app/home" : "/onboarding", { replace: true });
  }, [user, loading, navigate]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (googleMode === "own") {
      window.location.href = `${API}/auth/google/login`;
      return;
    }
    const redirectUrl = window.location.origin + "/onboarding";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0A0908]"><div className="flex gap-2" data-testid="login-loading"><span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" /><span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" /><span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" /></div></div>;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0908] bg-noise px-5 py-10" data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md text-center">
        <span className="font-display text-4xl text-gold-gradient">{BRAND.name}</span>
        <h1 className="mt-6 font-display text-3xl font-medium tracking-tight text-[#F4F1EA]">Welcome</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#C8C2B5]">{BRAND.tagline}. Sign in to meet a real stranger or a fictional AI persona.</p>
        {authError && (
          <p className="mt-4 rounded-xl border border-[#5C1D24]/60 bg-[#361217]/40 px-4 py-3 text-sm text-[#F4F1EA]" data-testid="login-auth-error">
            We couldn't sign you in. Please try again.
          </p>
        )}
        <Button data-testid="continue-with-google" onClick={login} className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#F4F1EA] py-6 text-base font-semibold text-[#0A0908] hover:bg-white">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continue with Google
        </Button>
        <p className="mt-4 text-xs leading-relaxed text-[#8C8578]">{AI_DISCLAIMER}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-[#8C8578]">
          <Link to="/" className="hover:text-[#D4AF37]" data-testid="login-back">Back</Link>
          <Link to="/privacy" className="hover:text-[#D4AF37]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#D4AF37]">Terms</Link>
          <Link to="/safety" className="hover:text-[#D4AF37]">Safety</Link>
        </div>
      </motion.div>
    </div>
  );
}
